import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileField } from "@/components/FileField";
import { DOC_TYPES, TREATMENTS } from "@/lib/ops";
import { errMsg, useApi } from "@/lib/api";

const F = ({ label, children }) => <div className="space-y-2"><Label className="text-slate-700">{label}</Label>{children}</div>;

const useSubmit = (txId, path, onDone) => {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const submit = async (body) => {
    setBusy(true);
    try {
      await api("post", `/transactions/${txId}/${path}`, body);
      toast.success("Etapa registrada en la cadena de custodia");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return [busy, submit];
};

const Sign = ({ name, setName, ok, setOk, text, testId }) => (
  <div className="space-y-3 bg-[#f4f7f6] rounded-xl p-4">
    <F label="Nombre del firmante"><Input className="h-11 bg-white" value={name} onChange={(e) => setName(e.target.value)} data-testid={`${testId}-signer-input`} /></F>
    <label className="flex items-start gap-3 text-sm text-slate-700"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} className="mt-0.5" data-testid={`${testId}-sign-checkbox`} />{text}</label>
  </div>
);

export const ScheduleForm = ({ tx, onDone }) => {
  const [f, setF] = useState({ pickup_date: "", pickup_time: "09:00", address: `${tx.lot.comuna}, ${tx.lot.region}`, volume_tons: tx.lot.weight_tons, transporter: "", plate: "" });
  const [busy, submit] = useSubmit(tx.id, "schedule", onDone);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form className="grid sm:grid-cols-2 gap-5" onSubmit={(e) => { e.preventDefault(); submit({ ...f, volume_tons: Number(f.volume_tons) }); }} data-testid="schedule-form">
      <F label="Fecha de retiro"><Input type="date" required className="h-11" value={f.pickup_date} onChange={set("pickup_date")} data-testid="schedule-date-input" /></F>
      <F label="Hora"><Input type="time" required className="h-11" value={f.pickup_time} onChange={set("pickup_time")} data-testid="schedule-time-input" /></F>
      <div className="sm:col-span-2"><F label="Ubicación exacta de retiro"><Input required className="h-11" value={f.address} onChange={set("address")} data-testid="schedule-address-input" /></F></div>
      <F label="Volumen reservado (t)"><Input type="number" step="0.01" min="0" required className="h-11 font-mono" value={f.volume_tons} onChange={set("volume_tons")} data-testid="schedule-volume-input" /></F>
      <F label="Transportista (opcional)"><Input className="h-11" value={f.transporter} onChange={set("transporter")} data-testid="schedule-transporter-input" /></F>
      <F label="Patente (opcional)"><Input className="h-11 font-mono uppercase" value={f.plate} onChange={set("plate")} data-testid="schedule-plate-input" /></F>
      <div className="sm:col-span-2 flex justify-end"><Button disabled={busy} className="rounded-full px-6 font-semibold" data-testid="schedule-submit-btn">Emitir Orden de Retiro</Button></div>
    </form>
  );
};

export const PickupQR = ({ code }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    QRCode.toDataURL(`TERRALOOP-RETIRO:${code}`, { margin: 1, width: 220, color: { dark: "#05130e", light: "#ffffff" } }).then(setSrc);
  }, [code]);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-6 bg-[#0a1f18] rounded-2xl p-6 text-white" data-testid="pickup-qr">
      {src && <img src={src} alt="QR de retiro" className="w-40 h-40 rounded-xl bg-white p-2" />}
      <div>
        <p className="text-sm text-slate-300">Muestra este QR o dicta el código al generador al llegar al retiro.</p>
        <p className="font-mono text-3xl font-bold text-[#00f2a1] tracking-[0.3em] mt-2" data-testid="pickup-code">{code}</p>
      </div>
    </div>
  );
};

export const PickupForm = ({ tx, onDone }) => {
  const [code, setCode] = useState("");
  const [w, setW] = useState(tx.schedule?.volume_tons || "");
  const [name, setName] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, submit] = useSubmit(tx.id, "pickup", onDone);
  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); submit({ code: code.replace("TERRALOOP-RETIRO:", ""), final_weight_tons: Number(w), signer_name: name, accept: ok }); }} data-testid="pickup-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <F label="Código QR del retiro (lo muestra el transportista)"><Input required className="h-11 font-mono uppercase tracking-widest" value={code} onChange={(e) => setCode(e.target.value)} data-testid="pickup-code-input" /></F>
        <F label="Peso / conteo final (t)"><Input type="number" step="0.001" min="0" required className="h-11 font-mono" value={w} onChange={(e) => setW(e.target.value)} data-testid="pickup-weight-input" /></F>
      </div>
      <Sign name={name} setName={setName} ok={ok} setOk={setOk} testId="pickup" text="Firmo digitalmente el Comprobante de Retiro: el lote fue pesado y entregado al transportista indicado." />
      <div className="flex justify-end"><Button disabled={busy || !ok} className="rounded-full px-6 font-semibold" data-testid="pickup-submit-btn">Validar retiro</Button></div>
    </form>
  );
};

export const ReceiveForm = ({ tx, onDone }) => {
  const [w, setW] = useState(tx.pickup?.peso_final_t || "");
  const [q, setQ] = useState(true);
  const [notes, setNotes] = useState("");
  const [treatment, setTreatment] = useState("");
  const [name, setName] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, submit] = useSubmit(tx.id, "receive", onDone);
  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); submit({ received_weight_tons: Number(w), quality_ok: q, notes, treatment, signer_name: name, accept: ok }); }} data-testid="receive-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <F label="Peso recibido en planta (t)"><Input type="number" step="0.001" min="0" required className="h-11 font-mono" value={w} onChange={(e) => setW(e.target.value)} data-testid="receive-weight-input" /></F>
        <F label="Tipo de tratamiento / valorización">
          <Select value={treatment} onValueChange={setTreatment}>
            <SelectTrigger className="h-11" data-testid="receive-treatment-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
            <SelectContent>{TREATMENTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </F>
      </div>
      <label className="flex items-center gap-3 text-sm text-slate-700"><Checkbox checked={q} onCheckedChange={(v) => setQ(!!v)} data-testid="receive-quality-checkbox" />Volumen y calidad conformes</label>
      <F label="Observaciones"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="receive-notes-input" /></F>
      <Sign name={name} setName={setName} ok={ok} setOk={setOk} testId="receive" text="Firmo digitalmente la recepción en destino y declaro que el material se integrará al proceso de valorización indicado." />
      <div className="flex justify-end"><Button disabled={busy || !ok || !treatment} className="rounded-full px-6 font-semibold" data-testid="receive-submit-btn">Confirmar recepción</Button></div>
    </form>
  );
};

export const CloseForm = ({ tx, onDone }) => {
  const [type, setType] = useState(tx.mode === "donacion" ? DOC_TYPES[3] : DOC_TYPES[0]);
  const [num, setNum] = useState("");
  const [file, setFile] = useState(null);
  const [busy, submit] = useSubmit(tx.id, "close", onDone);
  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); submit({ doc_type: type, doc_number: num, file_id: file }); }} data-testid="close-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <F label="Tipo de documento">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-11" data-testid="close-doctype-select"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        {type !== DOC_TYPES[3] && <F label="Folio / número"><Input required className="h-11 font-mono" value={num} onChange={(e) => setNum(e.target.value)} data-testid="close-docnumber-input" /></F>}
      </div>
      {type !== DOC_TYPES[3] && <F label="Adjuntar PDF del documento (emitido en el SII o tu sistema)"><FileField value={file} onChange={setFile} accept="application/pdf" testId="close-doc-file" label="Subir PDF" /></F>}
      <p className="text-xs text-slate-500">La emisión automática de documentos con el SII aún no está conectada: por ahora se registra el folio y se adjunta el PDF.</p>
      <div className="flex justify-end"><Button disabled={busy} className="rounded-full px-6 font-semibold" data-testid="close-submit-btn">Cerrar transacción y emitir certificado</Button></div>
    </form>
  );
};
